import React from 'react';
import './AboutPage.css';

const AboutPage: React.FC = () => {
  return (
    <main className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <div className="hero-content">
          <h1>About PolicyPal</h1>
          <p>Your trusted insurance partner committed to protecting what matters most to you and your family.</p>
        </div>
      </section>

      {/* Our Story */}
      <section className="our-story">
        <div className="container">
          <div className="story-content">
            <div className="story-text">
              <h2>Our Story</h2>
              <p>Founded in 2020, PolicyPal was born from a simple belief: insurance should be accessible, transparent, and customer-focused. We saw an industry bogged down by complexity and decided to change that.</p>
              
              <p>Our team of insurance experts and technology innovators came together to create a platform that puts customers first. We leverage cutting-edge AI technology to simplify the insurance process while maintaining the personal touch our customers deserve.</p>
              
              <p>Today, we're proud to serve thousands of families and businesses across the country, providing them with the protection and peace of mind they need to thrive.</p>
            </div>
            <div className="story-image">
              <div className="placeholder-image">🏢✨</div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="mission-vision">
        <div className="container">
          <div className="mv-grid">
            <div className="mv-card">
              <div className="mv-icon">🎯</div>
              <h3>Our Mission</h3>
              <p>To democratize insurance by making it simple, transparent, and accessible to everyone. We believe everyone deserves quality protection without the complexity.</p>
            </div>
            
            <div className="mv-card">
              <div className="mv-icon">👁️</div>
              <h3>Our Vision</h3>
              <p>To be the world's most trusted insurance platform, where technology and human care come together to protect what matters most.</p>
            </div>
            
            <div className="mv-card">
              <div className="mv-icon">💎</div>
              <h3>Our Values</h3>
              <ul>
                <li>Transparency in all we do</li>
                <li>Customer-first approach</li>
                <li>Innovation through technology</li>
                <li>Integrity and trust</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="team-section">
        <div className="container">
          <h2>Leadership Team</h2>
          <div className="team-grid">
            <div className="team-member">
              <div className="member-photo">👨‍💼</div>
              <div className="member-info">
                <h3>John Mitchell</h3>
                <p className="role">CEO & Founder</p>
                <p className="bio">Former insurance executive with 15+ years of experience. MIT graduate with a passion for using technology to solve real-world problems.</p>
              </div>
            </div>

            <div className="team-member">
              <div className="member-photo">👩‍💻</div>
              <div className="member-info">
                <h3>Sarah Chen</h3>
                <p className="role">CTO</p>
                <p className="bio">AI and machine learning expert from Stanford. Previously led engineering teams at major tech companies, bringing innovation to insurance.</p>
              </div>
            </div>

            <div className="team-member">
              <div className="member-photo">👨‍⚖️</div>
              <div className="member-info">
                <h3>Michael Rodriguez</h3>
                <p className="role">Chief Insurance Officer</p>
                <p className="bio">Licensed insurance professional with 20+ years in the industry. Expert in risk assessment and policy development.</p>
              </div>
            </div>

            <div className="team-member">
              <div className="member-photo">👩‍💼</div>
              <div className="member-info">
                <h3>Emily Watson</h3>
                <p className="role">VP of Customer Experience</p>
                <p className="bio">Customer service veteran committed to ensuring every PolicyPal interaction exceeds expectations. Harvard Business School MBA.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Company Stats */}
      <section className="company-stats">
        <div className="container">
          <h2>PolicyPal by the Numbers</h2>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number">50,000+</div>
              <div className="stat-label">Happy Customers</div>
            </div>
            
            <div className="stat-item">
              <div className="stat-number">$2.5M+</div>
              <div className="stat-label">Claims Paid</div>
            </div>
            
            <div className="stat-item">
              <div className="stat-number">98.5%</div>
              <div className="stat-label">Customer Satisfaction</div>
            </div>
            
            <div className="stat-item">
              <div className="stat-number">24/7</div>
              <div className="stat-label">Support Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* Awards & Recognition */}
      <section className="awards-section">
        <div className="container">
          <h2>Awards & Recognition</h2>
          <div className="awards-grid">
            <div className="award-item">
              <div className="award-icon">🏆</div>
              <h3>Best Insurance Innovation</h3>
              <p>InsurTech Awards 2023</p>
            </div>
            
            <div className="award-item">
              <div className="award-icon">⭐</div>
              <h3>Top Customer Service</h3>
              <p>National Insurance Review 2023</p>
            </div>
            
            <div className="award-item">
              <div className="award-icon">🚀</div>
              <h3>Fastest Growing InsurTech</h3>
              <p>TechCrunch 2022</p>
            </div>
            
            <div className="award-item">
              <div className="award-icon">🔒</div>
              <h3>Data Security Excellence</h3>
              <p>CyberSecurity Awards 2023</p>
            </div>
          </div>
        </div>
      </section>

      {/* Community Commitment */}
      <section className="community-section">
        <div className="container">
          <div className="community-content">
            <div className="community-text">
              <h2>Our Community Commitment</h2>
              <p>We believe in giving back to the communities we serve. PolicyPal is committed to making insurance education accessible and supporting local initiatives.</p>
              
              <div className="initiatives">
                <div className="initiative">
                  <h4>📚 Insurance Education Program</h4>
                  <p>Free workshops and resources to help people understand insurance and make informed decisions.</p>
                </div>
                
                <div className="initiative">
                  <h4>🌱 Environmental Responsibility</h4>
                  <p>Carbon-neutral operations and partnerships with eco-friendly organizations.</p>
                </div>
                
                <div className="initiative">
                  <h4>🤝 Community Partnerships</h4>
                  <p>Supporting local nonprofits and disaster relief efforts in communities across the country.</p>
                </div>
              </div>
            </div>
            <div className="community-image">
              <div className="placeholder-image">🌍❤️</div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology & Innovation */}
      <section className="technology-section">
        <div className="container">
          <h2>Technology & Innovation</h2>
          <div className="tech-grid">
            <div className="tech-item">
              <div className="tech-icon">🤖</div>
              <h3>AI-Powered Platform</h3>
              <p>Our advanced AI system provides personalized quotes, risk assessment, and instant customer support.</p>
            </div>
            
            <div className="tech-item">
              <div className="tech-icon">🔐</div>
              <h3>Bank-Grade Security</h3>
              <p>Enterprise-level encryption and security measures protect your personal and financial information.</p>
            </div>
            
            <div className="tech-item">
              <div className="tech-icon">📱</div>
              <h3>Mobile-First Design</h3>
              <p>Seamless experience across all devices with our responsive, mobile-optimized platform.</p>
            </div>
            
            <div className="tech-item">
              <div className="tech-icon">⚡</div>
              <h3>Real-Time Processing</h3>
              <p>Instant quotes, immediate policy updates, and real-time claim tracking for maximum convenience.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Join Us CTA */}
      <section className="join-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Join the PolicyPal Family</h2>
            <p>Experience the future of insurance with a company that puts you first. Join thousands of satisfied customers who trust PolicyPal.</p>
            <div className="cta-buttons">
              <button className="btn-primary">Get Started Today</button>
              <button className="btn-secondary">Learn More</button>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;